// ✅ src/pages/Products.jsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AllProductsThunk,
  DeleteProductThunk,
  AllSpecialcategoryThunk,
  MarkspecialThunk,
} from "../../redux/states/products/thunk";
import NonAuthLayout from "../../Layout/NonAuthLayout";
import { Box, Delete, Edit, Eye, Plus, Search } from "lucide-react";
import ConfirmDeleteModal from "../../Modals/ConfirmDeleteModal";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

const Products = () => {
  const dispatch = useDispatch();
  const { products, specialcategroy, loading } = useSelector(
    (state) => state.products
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedSpecial, setSelectedSpecial] = useState({});

  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    dispatch(AllProductsThunk());
    dispatch(AllSpecialcategoryThunk());
  }, [dispatch]);

  useEffect(() => {
    if (products && products.length > 0) {
      const filtered = products.filter(
        (product) =>
          product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.category_id_data
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase())
      );
      setFilteredProducts(filtered);

      // ✅ preselect already assigned special category
      const preSelected = {};
      products.forEach((p) => {
        if (p.special_category_id) {
          preSelected[p.uuid] = p.special_category_id;
        }
      });
      setSelectedSpecial(preSelected);
    }
  }, [products, searchTerm]);

  const handleDeleteClick = (uuid) => {
    setSelectedId(uuid);
    setModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedId) {
      dispatch(DeleteProductThunk(selectedId));
      setModalOpen(false);
      setSelectedId(null);
    }
  };

  const handleEdit = (uuid) => {
    navigate(`/update-new-products/${uuid}`);
  };

  const handleView = (uuid) => {
    navigate(`/view-products/${uuid}`);
  };

  const handleCreate = () => {
    navigate("/add-new-products");
    toast.success("Navigate to Add New Product");
  };

  const handleSpecialChange = (uuid, value) => {
    setSelectedSpecial((prev) => ({
      ...prev,
      [uuid]: value,
    }));
  };

  const handleMarkSpecial = (uuid) => {
    const sp_id = selectedSpecial[uuid];

    if (!sp_id) {
      toast.error("Please select a special category");
      return;
    }

    dispatch(
      MarkspecialThunk({
        uuid,
        special_category_id: sp_id,
      })
    )
      .unwrap()
      .then(() => {
        toast.success("Product marked as special successfully");
        dispatch(AllProductsThunk());
      })
      .catch(() => {
        toast.error("Failed to mark special category");
      });
  };

  return (
    <NonAuthLayout>
      <div className="min-h-screen p-1">

        {/* Header */}
        <div className="mb-2">
          <div className="bg-white rounded-2xl shadow-sm border p-6">
            <div className="flex justify-between">
              <div>
                <h1 className="text-3xl font-bold">Products</h1>
                <p>Total Products : {filteredProducts.length}</p>
              </div>
              <button
                onClick={handleCreate}
                className="bg-blue-600 text-white px-6 py-3 rounded-xl flex items-center"
              >
                <Plus className="mr-2" /> New Products
              </button>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="mb-2">
          <div className="relative">
            <Search className="absolute left-3 top-4 text-gray-400" />
            <input
              className="w-full pl-10 py-4 border rounded-2xl"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-2">
          {filteredProducts.length > 0 ? (
            filteredProducts.map((item, index) => {
              // ✅ YAHI SAHI JAGAH HAI
              const currentSpecialCategory = specialcategroy?.find(
                (sp) => sp.id === item.special_category_id
              );

              return (
                <div
                  key={item._id || index}
                  className="bg-white rounded-2xl shadow border overflow-hidden"
                >
                  <img
                    src={`${API_URL}/${item.image}`}
                    alt={item.name}
                    className="w-full h-48 object-cover"
                  />

                  <div className="p-4">
                    <h3 className="font-bold mb-1">
                      {item.name?.slice(0, 20)}
                    </h3>

                    {currentSpecialCategory && (
                      <p className="text-sm mb-2 text-green-700 font-semibold">
                        Already marked as:{" "}
                        <span className="text-green-900">
                          {currentSpecialCategory.name}
                        </span>
                      </p>
                    )}

                    <select
                      className="w-full border rounded px-2 py-1 mb-2"
                      value={selectedSpecial[item.uuid] || ""}
                      onChange={(e) =>
                        handleSpecialChange(item.uuid, e.target.value)
                      }
                    >
                      <option value="">Select Special Category</option>
                      {specialcategroy?.map((sp) => (
                        <option key={sp.id} value={sp.id}>
                          {sp.name}
                        </option>
                      ))}
                    </select>

                    <button
                      disabled={
                        item.special_category_id ==
                        selectedSpecial[item.uuid]
                      }
                      onClick={() => handleMarkSpecial(item.uuid)}
                      className={`w-full py-2 rounded ${
                        item.special_category_id ==
                        selectedSpecial[item.uuid]
                          ? "bg-gray-400"
                          : "bg-purple-600 text-white"
                      }`}
                    >
                      Mark Special
                    </button>

                        {/* Action Buttons */}
                  <div className="flex space-x-2 mt-2">
                    <button
                      onClick={() => handleView(item.uuid)}
                      className="flex-1 bg-blue-50 hover:bg-blue-100 text-blue-700 px-3 py-2 rounded-xl font-medium flex items-center justify-center transition-all duration-300 group/btn"
                      title="View Products"
                    >
                      <Eye className="mr-2 group-hover/btn:scale-110 transition-transform" />
                    </button>
                    <button
                      onClick={() => handleEdit(item.uuid)}
                      className="flex-1 bg-yellow-50 hover:bg-yellow-100 text-yellow-700 px-3 py-2 rounded-xl font-medium flex items-center justify-center transition-all duration-300 group/btn"
                      title="Edit Products"
                    >
                      <Edit className="mr-2 group-hover/btn:scale-110 transition-transform" />
                    </button>
                    <button
                      onClick={() => handleDeleteClick(item.uuid)}
                      className="flex-1 bg-red-50 hover:bg-red-100 text-red-700 px-3 py-2 rounded-xl font-medium flex items-center justify-center transition-all duration-300 group/btn"
                      title="Delete Products"
                    >
                      <Delete className="mr-2 group-hover/btn:scale-110 transition-transform" />
                    </button>
                  </div>
                  </div>
                </div>
              );
            })
          ) : (
            !loading && (
              <div className="col-span-full text-center py-10">
                <Box className="mx-auto text-gray-400" size={48} />
                <p>No Product Found</p>
              </div>
            )
          )}
        </div>
      </div>

      <ConfirmDeleteModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Product"
        message="Do you really want to delete this product?"
      />
    </NonAuthLayout>
  );
};

export default Products;
